"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { LeadRequest, postWebBmbyLead } from "api"
import { useRouter, useSearchParams } from "next/navigation"
import { LegacyRef, Suspense, useEffect, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { SubmitHandler, useForm } from "react-hook-form"
import colors from "theme/colors"
import { Container, NewText } from "ui"
import { submitError, submitSuccess } from "utils"

const FormField = styled.div`
  margin-bottom: 10px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    margin-bottom: 20px;
  }
`

const StyledTextField = styled.input`
  width: 100%;
  height: 50px;
  line-height: 106.6%;
  font-size: 16px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    height: 50px;
    font-size: 26px;
  }

  background-color: #e3ecf7;
  border: 1px solid white;

  padding-left: 16px;
  color: #1f3365;
  box-sizing: border-box;

  &::placeholder {
    color: #1f3365;
  }

  &:focus {
    outline: none;
    border-color: #1f3365;
  }
`

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 10px 0;
  ${(props) => props.theme.breakpoints.up("sm")} {
    gap: 5px;
    padding-inline-start: 20px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    margin: 10px 0;
  }
`

const Checkbox = styled.input<{ $hasError?: boolean }>`
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 0.75px solid ${(props) => (props.$hasError ? "#ff0000" : "#1f3365")};
  border-radius: 10px;
  background-color: #e3ecf7;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;

  /* Force square shape on iOS */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="checkbox"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 10px !important;
  }

  &:checked {
    background-color: #e3ecf7;
    border-color: ${(props) => (props.$hasError ? "#ff0000" : "#1f3365")};
    border-radius: 10px;
  }

  &:checked::after {
    content: "✓";
    position: absolute;
    top: 0px;
    left: 2px;
    color: ${(props) => (props.$hasError ? "#ff0000" : "#1f3365")};
    font-size: 12px;
    font-weight: bold;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px
      ${(props) => (props.$hasError ? "rgba(255, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)")};
  }

  ${(props) => props.theme.breakpoints.up("sm")} {
    width: 15px;
    height: 15px;
    border: 0.67px solid ${(props) => (props.$hasError ? "#ff0000" : "#1f3365")};

    &:checked::after {
      font-size: 12px;
      left: 2px;
      top: 0px;
    }
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    width: 15px;
    height: 15px;
    border: 1px solid ${(props) => (props.$hasError ? "#ff0000" : "#1f3365")};

    &:checked::after {
      font-size: 12px;
      left: 2px;
      top: 0px;
    }
  }
`

const CheckboxLabel = styled(NewText)`
  cursor: pointer;
`

const ErrorMessage = styled(NewText)`
  margin-top: 5px;
`

const SubmitButton = styled.button`
  width: 100%;
  height: 45px;
  background-color: #1988e2;
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #333;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
  ${(props) => props.theme.breakpoints.up("sm")} {
    width: 250px;
    align-self: flex-end;
    margin-top: auto;
    margin-bottom: 20px;
  }
`

const SubmitButtonText = styled(NewText)``

const FormTitle = styled(Typography)<{ component: string }>`
  color: #1988e2;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.1;
  margin-bottom: 10px;
  letter-spacing: -0.02em;
  ${(props) => props.theme.breakpoints.up("sm")} {
    font-size: 57px;
    margin-bottom: 20px;
  }
`

const StyledForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    padding: 0;
  }
`

const SubmissionSuccess = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: center;
  padding-top: 72px;
  margin-bottom: 72px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    margin-bottom: 0;

    padding-top: 25px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding-top: 39px;
  }
`

const FormContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  padding: 0 26px;
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 0 40px;
  }
`

const BottomContainer = styled.div`
  ${(props) => props.theme.breakpoints.down("sm")} {
    .grecaptcha-badge {
      z-index: -1;
      height: 45px !important;
    }
  }
`

const StyledLink = styled.a`
  color: inherit;
`

const StyledDisclaimer = styled.div`
  color: #1f3365;
  line-height: 1.1;
  font-size: 12px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    font-size: 22px;
    padding-inline-start: 20px;
  }
`

const StyledFormInner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("sm")} {
    width: 50%;
  }
`

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  console.warn("RECAPTCHA_KEY environment variable is not provided.")
}

interface FormData {
  fullName: string
  email: string
  phone: string
  allowedMarketing: boolean
  allowedSmsAndEmail: boolean
}

type Props = {
  companyId?: number
}

// eslint-disable-next-line no-unused-vars
function SidebarFormInner({ companyId }: Props) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [submitted, setSubmitted] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()

  function scrollToLeadForm() {
    if (typeof window === "undefined") return
    const el = document.getElementById("lead-success")
    if (el) {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      } catch (e) {
        // Fallback
        const top = el.getBoundingClientRect().top + window.scrollY
        window.scrollTo({ top, behavior: "smooth" })
      }
    }
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  // Check for thankyou parameter on component mount
  useEffect(() => {
    const thankyouParam = searchParams.get("thankyou")
    if (thankyouParam === "true") {
      setSubmitted(true)
    }
  }, [searchParams])

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    // Clear previous error state
    setSubmitFailed(false)
    setErrorMessage("")

    if (RECAPTCHA_KEY) {
      reCaptchaRef?.current?.reset()
    }

    let recaptchaResponse = ""
    if (RECAPTCHA_KEY) {
      try {
        recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
      } catch (error) {
        console.warn("reCAPTCHA execution failed:", error)
      }
    }

    try {
      const senddata: LeadRequest = {
        projectId: 11729, // As requested
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        comments: "",
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "",
        allowedMarketing: data.allowedMarketing || false,
        allowedSmsAndEmail: data.allowedSmsAndEmail || false,
        companyId: 5254, // As requested
        uri: window?.location?.href,
        toEmail: "", // Add appropriate value or leave empty if not needed
        intIn: "",
        mIkum: "",
      }

      const response = await postWebBmbyLead(senddata)
      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", "תודה שפניתם אלינו!", true)
        setSubmitted(true)
        reset()

        // Add thankyou parameter to URL
        const url = new URL(window.location.href)
        url.searchParams.set("thankyou", "true")
        router.push(url.pathname + url.search)

        setTimeout(() => {
          scrollToLeadForm()
        })
      } else {
        setSubmitFailed(true)
        setErrorMessage(response?.data?.body?.errorMessage || "שגיאה בשליחת הטופס")
        submitError("שליחה", "שגיאה בשליחת הטופס", response?.data?.body?.errorMessage)
      }
    } catch (error) {
      setSubmitFailed(true)
      setErrorMessage("שגיאה בשליחת הטופס")
      reset()
      submitError("שליחה", "שגיאה בשליחת הטופס", error as any)
    }
  }

  return (
    <>
      {submitted ? (
        <Container style={{ marginTop: 0, marginBottom: 0 }} id="lead-success">
          <SubmissionSuccess>
            <NewText
              mobileFontSize={50}
              mobileFontWeight={800}
              desktopLineHeight={"100%"}
              desktopFontWeight={300}
              component={"h3"}
              desktopFontSize={92}
              smallDesktopFontSize={70}
              color="#1988e2"
            >
              תודה שיצרת עימנו קשר
              <br />
              נציג מטעמנו יחזור אליך בהקדם
            </NewText>
          </SubmissionSuccess>
        </Container>
      ) : (
        <FormContainer id="lead-form">
          <FormTitle component="h2">השאר/י פרטים וניפגש לדבר על הבית החדש שלך</FormTitle>
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <StyledFormInner>
              <FormField>
                <StyledTextField
                  {...register("fullName", {
                    required: "שם מלא נדרש",
                  })}
                  placeholder="שם מלא (שדה חובה)"
                  type="text"
                />
                {errors.fullName && (
                  <ErrorMessage
                    component="div"
                    mobileFontSize={13}
                    smallDesktopFontSize={14}
                    desktopFontSize={14}
                    mobileLineHeight="1.2em"
                    smallDesktopLineHeight="1.2em"
                    desktopLineHeight="1.2em"
                    desktopFontWeight={400}
                    color={colors.red}
                  >
                    {errors.fullName.message}
                  </ErrorMessage>
                )}
              </FormField>
              <FormField>
                <StyledTextField
                  {...register("email", {
                    required: "אימייל נדרש",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "כתובת אימייל לא תקינה",
                    },
                  })}
                  placeholder="אימייל (שדה חובה)"
                  type="email"
                />
                {errors.email && (
                  <ErrorMessage
                    component="div"
                    mobileFontSize={13}
                    smallDesktopFontSize={14}
                    desktopFontSize={14}
                    mobileLineHeight="1.2em"
                    smallDesktopLineHeight="1.2em"
                    desktopLineHeight="1.2em"
                    desktopFontWeight={400}
                    color={colors.red}
                  >
                    {errors.email.message}
                  </ErrorMessage>
                )}
              </FormField>

              <FormField>
                <StyledTextField
                  {...register("phone", {
                    required: "טלפון נייד נדרש",
                  })}
                  placeholder="טלפון נייד (שדה חובה)"
                />
                {errors.phone && (
                  <ErrorMessage
                    component="div"
                    mobileFontSize={13}
                    smallDesktopFontSize={14}
                    desktopFontSize={14}
                    mobileLineHeight="1.2em"
                    smallDesktopLineHeight="1.2em"
                    desktopLineHeight="1.2em"
                    desktopFontWeight={400}
                    color={colors.red}
                  >
                    {errors.phone.message}
                  </ErrorMessage>
                )}
              </FormField>
            </StyledFormInner>
            <StyledFormInner>
              <StyledDisclaimer>
                בשליחת טופס זה אני מאשר/ת שקראתי את{" "}
                <StyledLink
                  href="https://www.ashtrom.co.il/content/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  מדיניות הפרטיות
                </StyledLink>{" "}
                של קבוצת אשטרום בע&quot;מ, לרבות של החברות הבנות ואת{" "}
                <StyledLink
                  href="https://www.ashtrom.co.il/content/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  תנאי השימוש
                </StyledLink>
                .
              </StyledDisclaimer>
              <CheckboxContainer>
                <Checkbox
                  id="allowedMarketing"
                  type="checkbox"
                  $hasError={!!errors.allowedMarketing}
                  {...register("allowedMarketing", {
                    required: "שדה חובה",
                  })}
                />
                <CheckboxLabel
                  component="span"
                  mobileFontSize={12}
                  smallDesktopFontSize={22}
                  desktopFontSize={22}
                  desktopLineHeight="106.6%"
                  desktopFontWeight={400}
                  color="#1f3365"
                >
                  מקובל עלי לקבל מקבוצת אשטרום בע״מ ו/או החברות הבת ו/או מי מטעמן פניות שיווקיות.
                </CheckboxLabel>
              </CheckboxContainer>
              {errors.allowedMarketing && (
                <ErrorMessage
                  component="div"
                  mobileFontSize={13}
                  smallDesktopFontSize={14}
                  desktopFontSize={14}
                  mobileLineHeight="1.2em"
                  smallDesktopLineHeight="1.2em"
                  desktopLineHeight="1.2em"
                  desktopFontWeight={400}
                  color={colors.red}
                  style={{ paddingInlineStart: "20px" }}
                >
                  {errors.allowedMarketing.message}
                </ErrorMessage>
              )}
              <CheckboxContainer>
                <Checkbox
                  id="allowedSmsAndEmail"
                  type="checkbox"
                  $hasError={!!errors.allowedSmsAndEmail}
                  {...register("allowedSmsAndEmail")}
                />
                <CheckboxLabel
                  component="span"
                  mobileFontSize={12}
                  smallDesktopFontSize={22}
                  desktopFontSize={22}
                  desktopLineHeight="106.6%"
                  desktopFontWeight={400}
                  color="#1f3365"
                >
                  מקובל עלי לקבל מקבוצת אשטרום בע״מ ו/או חברות הבת ו/או מי מטעמן תוכן פרסומי באמצעים
                  אלקטרוניים כמו SMS, דוא״ל, חיוג אוטומטי ועוד.
                </CheckboxLabel>
              </CheckboxContainer>
              {errors.allowedSmsAndEmail && (
                <ErrorMessage
                  component="div"
                  mobileFontSize={13}
                  smallDesktopFontSize={14}
                  desktopFontSize={14}
                  mobileLineHeight="1.2em"
                  smallDesktopLineHeight="1.2em"
                  desktopLineHeight="1.2em"
                  desktopFontWeight={400}
                  color={colors.red}
                  style={{ paddingInlineStart: "20px" }}
                >
                  {errors.allowedSmsAndEmail.message}
                </ErrorMessage>
              )}

              <SubmitButton type="submit" disabled={isSubmitting}>
                <SubmitButtonText
                  component="span"
                  mobileFontSize={18}
                  smallDesktopFontSize={22}
                  desktopFontSize={22}
                  mobileLineHeight="1.066em"
                  smallDesktopLineHeight="1.066em"
                  desktopLineHeight="1.066em"
                  desktopFontWeight={600}
                  color="white"
                >
                  {isSubmitting ? "שולח..." : "לקביעת פגישה >"}
                </SubmitButtonText>
              </SubmitButton>
            </StyledFormInner>

            {submitFailed && (
              <ErrorMessage
                component="div"
                mobileFontSize={15}
                smallDesktopFontSize={16}
                desktopFontSize={16}
                mobileLineHeight="1.2em"
                smallDesktopLineHeight="1.2em"
                desktopLineHeight="1.2em"
                desktopFontWeight={400}
                color={colors.red}
                style={{ marginTop: "15px", textAlign: "center" }}
              >
                {errorMessage} - אנא נסו שוב
              </ErrorMessage>
            )}

            <BottomContainer>
              {RECAPTCHA_KEY && (
                <ReCAPTCHA
                  ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                  sitekey={RECAPTCHA_KEY}
                  size="invisible"
                  hl="iw"
                  tabIndex={-1}
                  badge="bottomleft"
                />
              )}
            </BottomContainer>
          </StyledForm>
        </FormContainer>
      )}
    </>
  )
}

// Loading component for Suspense fallback
const FormLoading = () => (
  <FormContainer id="lead-form">
    <FormTitle component="h2">טוען...</FormTitle>
  </FormContainer>
)

// Main export component wrapped with Suspense
export default function SidebarForm({ companyId }: Props) {
  return (
    <Suspense fallback={<FormLoading />}>
      <SidebarFormInner companyId={companyId} />
    </Suspense>
  )
}
